import React from 'react'
import { Building2 } from 'lucide-react'
import {
  CVRParticipant,
  FoundingParentManagementSnapshot,
  OwnershipGraphData,
} from '../../types'
import CompanyOwnershipStrip from './CompanyOwnershipStrip'
import OwnershipGraph from './OwnershipGraph'

interface EjerskabOverviewProps {
  founders: CVRParticipant[]
  foundingManagementAtParents?: FoundingParentManagementSnapshot[]
  legalOwners: CVRParticipant[]
  beneficialOwners: CVRParticipant[]
  /** Flat list of all participants on the root company (for graph node panel). */
  rootParticipants?: CVRParticipant[]
  /** Ownership graph data from the /graph endpoint. */
  graph?: OwnershipGraphData | null
  /** True while the graph is still being fetched. */
  graphLoading?: boolean
  /** Founding date of the company (startdato from company details). */
  startdato?: string | null
}

function SectionHead({
  icon: Icon,
  title,
  hint,
  id,
}: {
  icon: React.ElementType
  title: string
  hint?: string
  id: string
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-4 w-4 shrink-0 text-ink-500" aria-hidden />
      <h2 id={id} className="text-sm font-semibold text-ink tracking-tight">
        {title}
        {hint ? (
          <span className="text-xs font-normal text-ink-500 ml-1.5">{hint}</span>
        ) : null}
      </h2>
    </div>
  )
}

/**
 * Ejerskab-fanen:
 *
 *   1. Kompakt overblik — reelle ejere, ejere, stiftere (à la person Overblik)
 *   2. Ejerskabsstruktur — SVG-graf
 */
const EjerskabOverview: React.FC<EjerskabOverviewProps> = ({
  founders,
  foundingManagementAtParents = [],
  legalOwners,
  beneficialOwners,
  rootParticipants = [],
  graph = null,
  graphLoading = false,
  startdato = null,
}) => {
  const showGraphSection = graphLoading || graph !== null

  return (
    <div className="space-y-6">
      <CompanyOwnershipStrip
        beneficialOwners={beneficialOwners}
        legalOwners={legalOwners}
        founders={founders}
        foundingManagementAtParents={foundingManagementAtParents}
        startdato={startdato}
      />

      {showGraphSection && (
        <section aria-labelledby="ejerskab-graf-label">
          <SectionHead
            id="ejerskab-graf-label"
            icon={Building2}
            title="Ejerskabsstruktur"
            hint="kæden over selskabet"
          />
          <OwnershipGraph
            data={graph}
            loading={graphLoading}
            rootParticipants={rootParticipants}
            beneficialOwners={beneficialOwners}
          />
        </section>
      )}
    </div>
  )
}

export default EjerskabOverview
